import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../store/authStore'

const BACKEND_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000/api'

const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Token refresh state
let isRefreshing = false
let failedQueue: { resolve: (v: unknown) => void; reject: (e: unknown) => void; config: InternalAxiosRequestConfig }[] = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else {
      config.headers.Authorization = `Bearer ${token}`
      resolve(api(config))
    }
  })
  failedQueue = []
}

/**
 * Signed out for good: clear the tokens AND the persisted auth state, then
 * go to the login page. Clearing only the tokens left 'admin-auth' saying
 * isAuthenticated, so /login bounced back to /, whose first request 401ed
 * and redirected to /login again: an endless reload loop.
 */
function endSession(): void {
  // The store first, so its persisted copy cannot write the old session back.
  useAuthStore.setState({ user: null, token: null, isAuthenticated: false })
  localStorage.removeItem('admin_token')
  localStorage.removeItem('admin_refresh_token')
  localStorage.removeItem('admin-auth')
  window.location.href = '/login'
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('admin_refresh_token')

      if (!refreshToken) {
        endSession()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const res = await axios.post(`${BACKEND_URL}/auth/refresh`, { refreshToken })
        const newToken = res.data?.data?.token ?? res.data?.token
        const newRefresh = res.data?.data?.refreshToken ?? res.data?.refreshToken

        if (newToken) {
          // Through the store, which writes localStorage too. Writing only
          // localStorage left the store on the expired token, which Sign out
          // and the live map's socket then used.
          useAuthStore.getState().setSessionTokens(newToken, newRefresh)
          processQueue(null, newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        }
        // A 200 with no token is a refusal in disguise; everything queued
        // behind it used to hang forever.
        processQueue(error, null)
        endSession()
        return Promise.reject(error)
      } catch (refreshError) {
        // Only the server refusing the refresh ends the session. A dropped
        // connection or a timeout used to sign the admin out mid-task.
        const status = (refreshError as { response?: { status?: number } })?.response?.status
        const refused = status === 400 || status === 401 || status === 403
        // A password change on this tab saves a fresh pair and ends every
        // older token. A request that was refused just before that answer
        // arrived refreshes with the old token and is refused again; that
        // refusal is about the token already replaced, not this session.
        const storedRefresh = localStorage.getItem('admin_refresh_token')
        if (refused && storedRefresh && storedRefresh !== refreshToken) {
          const current = localStorage.getItem('admin_token')
          processQueue(null, current)
          originalRequest.headers.Authorization = `Bearer ${current}`
          return api(originalRequest)
        }
        processQueue(refreshError, null)
        if (refused) endSession()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
